import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  progressBox: {
    marginBottom: theme.spacing(2),
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  passed: {
    color: '#4caf50',
  },
  failed: {
    color: '#f44336',
  },
  listItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
}));

interface CheckItem {
  label: string;
  passed: boolean;
  loading?: boolean;
}

export const OnboardingChecklistCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const spec = (entity as any).spec ?? {};
  const links = (entity.metadata as any).links ?? [];

  const labelSelector = annotations['backstage.io/kubernetes-label-selector'];

  // ── Static checks ──
  const hasCiCd = !!annotations['gitlab.com/project-slug'];
  const hasArgocd = !!annotations['argocd/app-name'];
  const hasTechdocs = !!annotations['backstage.io/techdocs-ref'];
  const hasDescription = !!entity.metadata.description;
  const hasOwner = !!spec.owner;
  const hasLinks = Array.isArray(links) && links.length > 0;
  const hasK8sLabels = !!labelSelector;

  // ── Healthcheck (backend call) ──
  const [probeResult, setProbeResult] = useState<{ hasReadinessProbe: boolean; hasLivenessProbe: boolean } | null>(null);
  const [probeLoading, setProbeLoading] = useState(false);

  useEffect(() => {
    if (!labelSelector) return;

    // Get first environment namespace from config
    let namespace: string | undefined;
    try {
      const envs = configApi.getConfigArray('fondative.environments');
      if (envs.length > 0) {
        namespace = envs[0].getString('namespace');
      }
    } catch {
      // no environments configured
    }

    // Fallback to annotation namespace
    if (!namespace) {
      namespace = annotations['backstage.io/kubernetes-namespace'];
    }

    if (!namespace) return;

    setProbeLoading(true);
    fetchApi
      .fetch(
        `${backendBaseUrl}/api/onboarding-checklist/probes?namespace=${encodeURIComponent(namespace)}&labelSelector=${encodeURIComponent(labelSelector)}`,
      )
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => {
        setProbeResult(data);
        setProbeLoading(false);
      })
      .catch(() => {
        setProbeResult({ hasReadinessProbe: false, hasLivenessProbe: false });
        setProbeLoading(false);
      });
  }, [labelSelector, annotations, backendBaseUrl, fetchApi, configApi]);

  const hasHealthcheck = probeResult ? probeResult.hasReadinessProbe || probeResult.hasLivenessProbe : false;

  const checks: CheckItem[] = [
    { label: 'CI/CD configuré', passed: hasCiCd },
    { label: 'ArgoCD configuré', passed: hasArgocd },
    { label: 'Documentation', passed: hasTechdocs },
    { label: 'Description', passed: hasDescription },
    { label: 'Propriétaire', passed: hasOwner },
    { label: 'Liens', passed: hasLinks },
    { label: 'Healthcheck', passed: hasHealthcheck, loading: probeLoading },
    { label: 'Labels K8s', passed: hasK8sLabels },
  ];

  const passedCount = checks.filter(c => c.passed).length;
  const total = checks.length;
  const percentage = Math.round((passedCount / total) * 100);

  return (
    <Card>
      <CardHeader title="Checklist d'onboarding" />
      <CardContent>
        <Box className={classes.progressBox}>
          <Box className={classes.progressLabel}>
            <Typography variant="body2" color="textSecondary">
              {passedCount}/{total} configurés
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {percentage}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={percentage} />
        </Box>
        <List dense disablePadding>
          {checks.map(check => (
            <ListItem key={check.label} className={classes.listItem} disableGutters>
              <ListItemIcon style={{ minWidth: 32 }}>
                {check.loading ? (
                  <CircularProgress size={20} />
                ) : check.passed ? (
                  <CheckCircleIcon className={classes.passed} fontSize="small" />
                ) : (
                  <CancelIcon className={classes.failed} fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primary={check.label} />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
